import { useState } from "react";
import { useGeoCurrency } from "@/hooks/useGeoCurrency";
import { SN_MembershipCTA } from "./SN_MembershipCTA";
import { useAuth } from "@/hooks/use-auth";
import { useNavigate } from "react-router-dom";
import { ClubFoundingModal as FoundingMemberModal } from "@/components/club/ClubCheckout";
import { PaymentProcessorsBadge } from "@/components/icons/PaymentLogos";
import { Section, SectionHead } from "@/components/sn/SectionHead";

type Billing = "monthly" | "annual";

// The plans' lines, shared with the Club page (26 Oct 2026).
export const premiumIncludes = [
  "Full access to every playbook and recording",
  "New drops every week, member community access",
  "The Alpha with AI newsletter at its highest tier, granted on your membership email",
  "US and global stock research, plus the model book of dated entries",
  "Cancel anytime from your dashboard.",
];
export const foundingIncludes = [
  "Everything in Club Premium",
  "A permanent badge and your name on the public Founding Wall",
  "Priority seats in every masterclass, and a 1:1 call each quarter",
  "Custom workflow requests and a private founder letter",
];

// The purchase logic here (the CTA, the founding modal, the sign-in redirect)
// is untouched from the previous version. Only the layout changed: two
// hairline columns instead of two glowing cards, mono numbers, one toggle.
export const SN_MembershipPricingCompare = ({ label = "04 · Membership pricing" }: { label?: string }) => {
  const { subscriptionPrices, isIndian } = useGeoCurrency();
  const monthly = subscriptionPrices.base.monthly;
  const annual = subscriptionPrices.base.annual;
  const perMonthAnnualised = isIndian ? "₹833" : "$33";

  const [billing, setBilling] = useState<Billing>("annual");
  const isAnnual = billing === "annual";
  const [foundingOpen, setFoundingOpen] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();
  const foundingPrice = isIndian ? "₹39,999" : "$999";

  const handleFoundingClick = () => {
    if (!user) {
      const redirect = encodeURIComponent("/?founding=1");
      navigate(`/login?redirect=${redirect}`);
      return;
    }
    setFoundingOpen(true);
  };

  const toggleButton = (active: boolean) =>
    `h-[26px] rounded-md px-2.5 text-[12px] font-medium transition-colors ${
      active ? "bg-foreground text-background" : "text-muted-foreground hover:text-foreground"
    }`;

  return (
    <Section id="pricing">
      <SectionHead
        label={label}
        title="Membership pricing."
        lead="Auto-renews. Cancel anytime from your dashboard."
      />

      <div className="mt-8" role="tablist" aria-label="Billing interval">
        <div className="inline-flex gap-0.5 rounded-lg border border-[var(--sn-border-strong)] p-0.5">
          <button role="tab" aria-selected={!isAnnual} onClick={() => setBilling("monthly")} className={toggleButton(!isAnnual)}>
            Monthly
          </button>
          <button role="tab" aria-selected={isAnnual} onClick={() => setBilling("annual")} className={toggleButton(isAnnual)}>
            Annual
            <span className="ml-1.5 text-primary">saves about 17%</span>
          </button>
        </div>
      </div>

      <div className="mt-10 grid gap-x-14 gap-y-12 md:grid-cols-2">
        {/* Club Premium */}
        <div className="flex flex-col border-t border-primary pt-6">
          <p className="sn-label">Alpha with AI Club · Premium</p>
          <div className="mt-4 flex items-baseline gap-2">
            <span className="text-[44px] font-semibold tracking-[-0.035em] text-foreground [font-variant-numeric:tabular-nums]">
              {isAnnual ? annual : monthly}
            </span>
            <span className="text-[15px] text-muted-foreground">/ {isAnnual ? "year" : "month"}</span>
          </div>
          <p className="mt-1.5 text-[13.5px] text-muted-foreground">
            {isAnnual ? `${perMonthAnnualised}/month equivalent. Billed annually.` : "Billed monthly. Cancel anytime."}
            {isIndian && " Exclusive of GST."}
          </p>

          <ul className="mt-6 flex-1 list-none space-y-0 p-0">
            {premiumIncludes.map((line) => (
              <li key={line} className="border-t border-[var(--sn-border)] py-2.5 text-[14px] leading-[1.5] text-[var(--sn-fg2)]">
                {line}
              </li>
            ))}
          </ul>

          <SN_MembershipCTA
            showIcon={false}
            startLabel="Start membership"
            className="mt-7 inline-flex h-10 w-full items-center justify-center rounded-lg bg-primary px-[18px] text-[14px] font-semibold text-primary-foreground transition hover:brightness-110 disabled:opacity-70"
          />
        </div>

        {/* Founding Member */}
        <div className="flex flex-col border-t border-[var(--sn-border-strong)] pt-6">
          <p className="sn-label">Founding Member · Price locked for life</p>
          <div className="mt-4 flex items-baseline gap-2">
            <span className="text-[44px] font-semibold tracking-[-0.035em] text-foreground [font-variant-numeric:tabular-nums]">
              {foundingPrice}
            </span>
            <span className="text-[15px] text-muted-foreground">/ year</span>
          </div>
          <p className="mt-1.5 text-[13.5px] text-muted-foreground">
            Auto-renews annually. Your founding rate is locked for life.
            {isIndian && " Exclusive of GST."}
          </p>

          <ul className="mt-6 flex-1 list-none space-y-0 p-0">
            {foundingIncludes.map((line) => (
              <li key={line} className="border-t border-[var(--sn-border)] py-2.5 text-[14px] leading-[1.5] text-[var(--sn-fg2)]">
                {line}
              </li>
            ))}
          </ul>

          <button
            type="button"
            onClick={handleFoundingClick}
            className="mt-7 inline-flex h-10 w-full items-center justify-center rounded-lg border border-[var(--sn-border-strong)] px-[18px] text-[14px] font-semibold text-foreground transition-colors hover:border-primary hover:text-primary"
          >
            Become a Founding Member
          </button>
        </div>
      </div>

      <FoundingMemberModal isOpen={foundingOpen} onClose={() => setFoundingOpen(false)} />

      <div className="mt-10 flex flex-wrap items-start justify-between gap-6 border-t border-[var(--sn-border)] pt-5">
        <p className="m-0 max-w-[62ch] text-[12.5px] leading-[1.55] text-muted-foreground">
          The model portfolio is a model book. The positions in it are hypothetical and no real capital sits
          behind them. Nothing on this page is investment advice.
        </p>
        <PaymentProcessorsBadge />
      </div>
    </Section>
  );
};
